// src/stores/currencyStore.ts
// Zustand store for currency management with exchange rates
// Uses centralized API client for consistent error handling

import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import {
  safeGetItem,
  safeSetItem,
  StorageKeys,
  StorageTTL,
} from '@/lib/storage/safeStorage';
import { cache, CacheTTL, CacheTags } from '@/lib/cache/UnifiedCache';
import { currencyApi } from '@/lib/api/apiClient';
import { handleError, NetworkError } from '@/lib/errors/AppError';

// ============= Types =============
export type Currency = 'EUR' | 'USD' | 'GBP' | 'MAD';

interface ExchangeRates {
  EUR: Record<Currency, number>;
  USD: Record<Currency, number>;
  GBP: Record<Currency, number>;
  MAD: Record<Currency, number>;
}

interface CurrencyState {
  // State
  currency: Currency;
  exchangeRates: ExchangeRates;
  isLoading: boolean;
  lastUpdated: number | null;

  // Actions
  setCurrency: (currency: Currency) => void;
  fetchExchangeRates: () => Promise<void>;
  refreshRates: () => Promise<void>;

  // Computed (as functions)
  convertPrice: (price: number, fromCurrency?: Currency) => number;
  formatPrice: (price: number, fromCurrency?: Currency) => string;
}

// ============= Constants =============
const DEFAULT_EXCHANGE_RATES: ExchangeRates = {
  EUR: { EUR: 1, USD: 1.09, GBP: 0.86, MAD: 10.85 },
  USD: { EUR: 0.92, USD: 1, GBP: 0.79, MAD: 9.95 },
  GBP: { EUR: 1.16, USD: 1.27, GBP: 1, MAD: 12.62 },
  MAD: { EUR: 0.092, USD: 0.1, GBP: 0.079, MAD: 1 },
};

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  MAD: 'DH',
};

const CURRENCY_LOCALE: Record<Currency, string> = {
  EUR: 'fr-FR',
  USD: 'en-US',
  GBP: 'en-GB',
  MAD: 'fr-MA',
};

const RATES_CACHE_KEY = 'exchange_rates';

// ============= Store =============
export const useCurrencyStore = create<CurrencyState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        currency: 'EUR',
        exchangeRates: DEFAULT_EXCHANGE_RATES,
        isLoading: false,
        lastUpdated: null,

        setCurrency: (currency) => {
          set({ currency });
          // Also persist to safeStorage for compatibility
          safeSetItem(StorageKeys.CURRENCY, currency, {
            ttl: StorageTTL.MONTH,
          });
        },

        fetchExchangeRates: async () => {
          try {
            // Try cache first (with safety check)
            if (cache && typeof cache.get === 'function') {
              const cached = cache.get<ExchangeRates>(RATES_CACHE_KEY);
              if (cached?.data && !cached.isExpired) {
                set({ exchangeRates: cached.data, lastUpdated: Date.now() });
                return;
              }
            }

            set({ isLoading: true });

            // Use centralized API client for consistent error handling
            const data = await currencyApi.get<{
              rates?: { USD?: number; GBP?: number };
            }>('/latest?from=EUR&to=USD,GBP');

            // Frankfurter API returns { rates: { USD: x, GBP: y } }
            if (!data.rates) {
              set({ isLoading: false, lastUpdated: Date.now() });
              return;
            }

            const rates = {
              USD: data.rates.USD ?? DEFAULT_EXCHANGE_RATES.EUR.USD,
              GBP: data.rates.GBP ?? DEFAULT_EXCHANGE_RATES.EUR.GBP,
              MAD: DEFAULT_EXCHANGE_RATES.EUR.MAD, // MAD not supported by frankfurter, use default
            };

            const newRates: ExchangeRates = {
              EUR: { EUR: 1, USD: rates.USD, GBP: rates.GBP, MAD: rates.MAD },
              USD: {
                EUR: 1 / rates.USD,
                USD: 1,
                GBP: rates.GBP / rates.USD,
                MAD: rates.MAD / rates.USD,
              },
              GBP: {
                EUR: 1 / rates.GBP,
                USD: rates.USD / rates.GBP,
                GBP: 1,
                MAD: rates.MAD / rates.GBP,
              },
              MAD: {
                EUR: 1 / rates.MAD,
                USD: rates.USD / rates.MAD,
                GBP: rates.GBP / rates.MAD,
                MAD: 1,
              },
            };

            // Cache the new rates (with safety check)
            if (cache && typeof cache.set === 'function') {
              cache.set(RATES_CACHE_KEY, newRates, {
                ttl: CacheTTL.HOUR,
                staleTime: CacheTTL.MEDIUM,
                tags: [CacheTags.PROFILE],
              });
            }

            set({
              exchangeRates: newRates,
              lastUpdated: Date.now(),
              isLoading: false,
            });
          } catch (error) {
            // Convert to AppError for consistent handling, but don't display to user
            const appError = handleError(error);

            // Log network errors for monitoring (could be sent to analytics)
            if (appError instanceof NetworkError) {
              console.debug(
                'Currency fetch failed, using defaults:',
                appError.code
              );
            }

            // Silently fall back to default rates
            set({ isLoading: false, lastUpdated: Date.now() });
          }
        },

        refreshRates: async () => {
          if (cache && typeof cache.invalidate === 'function') {
            cache.invalidate(RATES_CACHE_KEY);
          }
          await get().fetchExchangeRates();
        },

        convertPrice: (price, fromCurrency = 'EUR') => {
          const { currency, exchangeRates } = get();
          if (fromCurrency === currency) return price;
          const rate = exchangeRates[fromCurrency]?.[currency] ?? 1;
          return Math.round(price * rate * 100) / 100;
        },

        formatPrice: (price, fromCurrency = 'EUR') => {
          const { currency, convertPrice } = get();
          const convertedPrice = convertPrice(price, fromCurrency);

          try {
            return new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
              style: 'currency',
              currency: currency === 'MAD' ? 'MAD' : currency,
              minimumFractionDigits: currency === 'EUR' ? 0 : 2,
              maximumFractionDigits: 2,
            }).format(convertedPrice);
          } catch {
            const symbol = CURRENCY_SYMBOLS[currency];
            switch (currency) {
              case 'USD':
              case 'GBP':
                return `${symbol}${convertedPrice.toFixed(2)}`;
              case 'MAD':
                return `${convertedPrice.toFixed(2)} ${symbol}`;
              case 'EUR':
              default:
                return `${convertedPrice.toFixed(0)} ${symbol}`;
            }
          }
        },
      }),
      {
        name: 'currency-storage',
        version: 1,
        partialize: (state) => ({ currency: state.currency }),
        migrate: (persisted: any, version: number) => {
          try {
            if (version < 1) {
              const currency = persisted?.currency;
              if (currency && ['EUR', 'USD', 'GBP', 'MAD'].includes(currency)) {
                return { currency };
              }
              return { currency: 'EUR' };
            }
            return persisted;
          } catch {
            return { currency: 'EUR' };
          }
        },
        storage: {
          getItem: (name) => {
            try {
              const raw = localStorage.getItem(name);
              if (!raw) return null;
              return JSON.parse(raw);
            } catch {
              try {
                localStorage.removeItem(name);
              } catch {
                /* ignore */
              }
              return null;
            }
          },
          setItem: (name, value) => {
            try {
              localStorage.setItem(name, JSON.stringify(value));
            } catch {
              /* ignore */
            }
          },
          removeItem: (name) => {
            try {
              localStorage.removeItem(name);
            } catch {
              /* ignore */
            }
          },
        },
      }
    ),
    { name: 'currency-store' }
  )
);

// ============= Selectors =============
export const selectCurrency = (state: CurrencyState) => state.currency;
export const selectIsLoading = (state: CurrencyState) => state.isLoading;
export const selectLastUpdated = (state: CurrencyState) => state.lastUpdated;

// Action selectors (stable references)
const selectSetCurrency = (state: CurrencyState) => state.setCurrency;
const selectFormatPrice = (state: CurrencyState) => state.formatPrice;
const selectConvertPrice = (state: CurrencyState) => state.convertPrice;
const selectRefreshRates = (state: CurrencyState) => state.refreshRates;

// ============= Hook for compatibility =============
export const useCurrency = () => {
  const currency = useCurrencyStore(selectCurrency);
  const isLoading = useCurrencyStore(selectIsLoading);
  const lastUpdated = useCurrencyStore(selectLastUpdated);
  const setCurrency = useCurrencyStore(selectSetCurrency);
  const formatPrice = useCurrencyStore(selectFormatPrice);
  const convertPrice = useCurrencyStore(selectConvertPrice);
  const refreshRates = useCurrencyStore(selectRefreshRates);

  return {
    currency,
    setCurrency,
    formatPrice,
    convertPrice,
    isLoading,
    lastUpdated: lastUpdated ? new Date(lastUpdated) : null,
    refreshRates,
  };
};

// ============= Initialization =============
export const initializeCurrencyStore = () => {
  // Load persisted currency and fetch rates
  const saved = safeGetItem<Currency>(StorageKeys.CURRENCY);
  if (saved && ['EUR', 'USD', 'GBP', 'MAD'].includes(saved)) {
    useCurrencyStore.getState().setCurrency(saved);
  }
  useCurrencyStore.getState().fetchExchangeRates();
};
